import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import "./HandSummary.scss";

// v2-1438 step 3: the hand written out in the page of a replay (/w/), under the replayer. The replayer itself runs in an
// iframe (/a/), whose content search engines do not give to this page: this text is what they read of the hand. In a
// <details> closed by default: the text is in the page all the same.
// The player who published the hand is named (his pseudo at the table when the hand shows it, the name of his account
// when he made it anonymous, "hero"), as in the title of the page (social/opencards-seo.php); the others only by their
// position, never by their pseudo.
// The page's hand comes from details.php, which sends it without its actions (getReplay of social/opencards_lib.php):
// the summary loads the hand from hand.php, as the replayer does, but without log=true, which counts a view. Not for a
// whole session, which the replayer already loads entirely.

const RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K"];
const SUITS = ["♣", "♠", "♥", "♦"];

// a card of the replayer: 1-13 clubs, 14-26 spades, 27-39 hearts, 40-52 diamonds, the rank n % 13 (0 = A); null if unknown
export const cardText = (card) => {
  const n = Number(card);
  if (!Number.isInteger(n) || n < 1 || n > 52) {
    return null;
  }
  return RANKS[n % 13] + SUITS[Math.floor((n - 1) / 13)];
};

// the cards of a list, null when one of them is unknown
const knownCards = (cards) => {
  if (!Array.isArray(cards) || cards.length === 0) {
    return null;
  }
  const texts = [];
  for (const card of cards) {
    const text = cardText(card);
    if (text === null) {
      return null;
    }
    texts.push(text);
  }
  return texts.join(" ");
};

// the seats named by their distance from the button, as the software names them (v2-708): SB, BB, then counted back from
// the button BTN, CO, HJ, LJ, UTG+3, UTG+2, UTG+1, UTG; heads-up, the button posts the small blind
const LATE = ["BTN", "CO", "HJ", "LJ", "UTG+3", "UTG+2", "UTG+1", "UTG"];
export const seatPositions = (seats, buttonSeat) => {
  const sorted = [...seats].sort((a, b) => a - b);
  const order = sorted.filter((seat) => seat > buttonSeat).concat(sorted.filter((seat) => seat <= buttonSeat));
  const names = {};
  if (order.length === 2) {
    const button = order.includes(buttonSeat) ? buttonSeat : order[1];
    order.forEach((seat) => {
      names[seat] = seat === button ? "SB" : "BB";
    });
    return { order: [button, order.find((seat) => seat !== button)], names };
  }
  if (order.length < 2) {
    return { order, names };
  }
  names[order[0]] = "SB";
  names[order[1]] = "BB";
  for (let i = order.length - 1, k = 0; i >= 2; i--, k++) {
    names[order[i]] = LATE[Math.min(k, LATE.length - 1)];
  }
  return { order, names };
};

const VARIANTS = {
  NO_LIMIT_HOLD_EM: "No Limit Hold'em",
  LIMIT_HOLD_EM: "Limit Hold'em",
  POT_LIMIT_HOLD_EM: "Pot Limit Hold'em",
  LIMIT_OMAHA: "Limit Omaha",
  POT_LIMIT_OMAHA: "Pot Limit Omaha"
};
const CURRENCIES = { EURO: "EUR", DOLLAR: "USD", STERLING: "GBP", YEN: "JPY" };
// the names of the title of the page (ocseo_room of social/opencards-seo.php)
const ROOMS = {
  POKERSTARS: "PokerStars",
  FULLTILT: "FullTilt",
  EVEREST_BETCLIC_FR: "Everest/Betclic",
  PARTYGAMING: "PartyGaming",
  WINAMAX: "Winamax",
  IGAMING: "iGaming",
  ONGAME: "Ongame",
  BARRIERE: "Barrière",
  PARTOUCHE: "Partouche",
  CAKE_POKER: "Cake",
  MERGE_POKER: "Merge Poker",
  BODOG_POKER: "Bodog",
  NETWORK_888: "888 Network",
  PEOPLES_NETWORK: "Peoples Poker",
  GGPOKER: "GGPoker",
  BETCLIC: "Betclic",
  COINPOKER: "CoinPoker"
};

// the Windows-1252 bytes 0x80-0x9F that are not Latin-1, for unmangle
const CP1252 = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88,
  0x2030: 0x89, 0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93,
  0x201d: 0x94, 0x2022: 0x95, 0x2013: 0x96, 0x2014: 0x97, 0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b,
  0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f
};
// a session name stored twice encoded by an old client ("â‚¬NLHE10" for "€NLHE10"), read back once, as
// ocseo_unmangle of social/opencards-seo.php does
export const unmangle = (text) => {
  if (typeof text !== "string" || !/Ã|Â|â‚|â€/.test(text)) {
    return text;
  }
  let encoded = "";
  for (const char of text) {
    const code = char.codePointAt(0);
    const byte = code < 0x100 ? code : CP1252[code];
    if (byte === undefined) {
      return text;
    }
    encoded += "%" + ("0" + byte.toString(16)).slice(-2);
  }
  try {
    // throws on bytes that are not UTF-8: the text was right as it was
    return decodeURIComponent(encoded);
  } catch (e) {
    return text;
  }
};

const number = (lang, value, decimals) =>
  new Intl.NumberFormat(lang, { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(value);

/**
 * The lines of the summary of a hand in the language lang: [{ label, text }], or null when the hand lacks actions or
 * players. t is the translation function of react-i18next.
 */
export const buildHandSummary = (hand, t, lang) => {
  if (!hand || !Array.isArray(hand.actions) || hand.actions.length === 0) {
    return null;
  }
  const first = hand.actions[0];
  const last = hand.actions[hand.actions.length - 1];
  if (!first || !Array.isArray(first.players) || first.players.length === 0 || !last || !Array.isArray(last.players)) {
    return null;
  }
  const tournament = Number(hand.sessionType) === 1;
  const bigBlind = Number(hand.bigBlind) > 0 ? Number(hand.bigBlind) : 0;
  const currency = CURRENCIES[hand.currency];
  // an amount of the hand: money in cash, chips in a tournament
  const amount = (value) => {
    if (tournament || !currency) {
      return number(lang, value, tournament ? 0 : 2) + (tournament ? " " + t("replayApp.summary.chips") : "");
    }
    const cents = Math.abs(value - Math.round(value)) >= 0.005;
    return new Intl.NumberFormat(lang, {
      style: "currency",
      currency,
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0
    }).format(value);
  };
  // a bet in big blinds, or as an amount when the big blind is unknown
  const inBigBlinds = (value) => (bigBlind > 0 ? number(lang, value / bigBlind, 1) + " BB" : amount(value));

  const { order, names } = seatPositions(
    first.players.map((player) => Number(player.seat)),
    Number(hand.buttonSeat)
  );
  const heroName = typeof hand.hero === "string" ? hand.hero : null;
  const heroPlayer = heroName === null ? undefined : first.players.find((player) => player.pseudo === heroName);
  let publisher = null;
  if (heroPlayer) {
    publisher =
      heroName.trim() !== "" && heroName.trim().toLowerCase() !== "hero"
        ? unmangle(heroName)
        : typeof hand.user_name === "string" && hand.user_name !== ""
        ? hand.user_name
        : null;
  }
  const seatOf = {};
  first.players.forEach((player) => {
    seatOf[player.pseudo] = Number(player.seat);
  });
  // who acts: the publisher by his name, the others by their position
  const who = (pseudo) => {
    if (heroPlayer && publisher !== null && pseudo === heroName) {
      return publisher;
    }
    return names[seatOf[pseudo]] || t("replayApp.summary.player");
  };

  const lines = [];
  const game = [VARIANTS[hand.gameVariant] || "Poker"];
  if (bigBlind > 0) {
    game.push(
      tournament
        ? number(lang, Number(hand.smallBlind), 0) + "/" + number(lang, bigBlind, 0) +
            (Number(hand.ante) > 0 ? " (ante " + number(lang, Number(hand.ante), 0) + ")" : "")
        : amount(Number(hand.smallBlind)) + "/" + amount(bigBlind)
    );
  }
  const date =
    Number(hand.startDate) > 0
      ? new Intl.DateTimeFormat(lang, { year: "numeric", month: "long", day: "numeric" }).format(new Date(Number(hand.startDate)))
      : "";
  const session = typeof hand.sessionName === "string" ? unmangle(hand.sessionName).trim() : "";
  // the variables are not named "count": i18next would look for plural keys
  lines.push({
    text: t(session !== "" ? "replayApp.summary.intro" : "replayApp.summary.introNoSession", {
      game: game.join(" "),
      room: ROOMS[hand.roomType] || "Opencards",
      session,
      date,
      players: order.length
    })
  });

  // the players and their stacks at the start of the hand (the blinds are already on the table at the first action)
  const stacks = order
    .map((seat) => {
      const player = first.players.find((p) => Number(p.seat) === seat);
      if (!player) {
        return null;
      }
      const stack = Number(player.chips || 0) + Number(player.chipsOnTable || 0);
      const position = names[seat] || "";
      const label =
        heroPlayer && publisher !== null && player.pseudo === heroName ? publisher + (position ? " (" + position + ")" : "") : position;
      return label + " " + inBigBlinds(stack);
    })
    .filter((text) => text !== null);
  lines.push({ label: t("replayApp.summary.players"), text: stacks.join(", ") });
  if (heroPlayer && publisher !== null) {
    const heroCards = knownCards(last.players.find((p) => p.pseudo === heroName)?.cards);
    if (heroCards !== null) {
      lines.push({ label: t("replayApp.summary.cards", { who: publisher }), text: heroCards });
    }
  }

  // the streets, their board and their actions
  const streets = [];
  hand.actions.forEach((action) => {
    if (!action || ["PREFLOP", "FLOP", "TURN", "RIVER"].indexOf(action.step) < 0) {
      return;
    }
    let street = streets.length > 0 ? streets[streets.length - 1] : null;
    if (street === null || street.step !== action.step) {
      const board = Array.isArray(action.cards) ? action.cards : [];
      const cards = action.step === "FLOP" ? board.slice(0, 3) : action.step === "TURN" ? board.slice(3, 4) : action.step === "RIVER" ? board.slice(4, 5) : [];
      street = { step: action.step, board: knownCards(cards), actions: [] };
      streets.push(street);
    }
    const actor = action.pseudo;
    const player = Array.isArray(action.players) ? action.players.find((p) => p.pseudo === actor) : undefined;
    const onTable = player && Number(player.chipsOnTable) > 0 ? Number(player.chipsOnTable) : 0;
    const allin = player !== undefined && Number(player.chips) === 0;
    switch (action.eventType) {
      case "FOLD":
        street.actions.push(t("replayApp.summary.fold", { who: who(actor) }));
        break;
      case "CHECK":
        street.actions.push(t("replayApp.summary.check", { who: who(actor) }));
        break;
      case "CALL":
        street.actions.push(t(allin ? "replayApp.summary.callAllin" : "replayApp.summary.call", { who: who(actor) }));
        break;
      case "BET":
        street.actions.push(t(allin ? "replayApp.summary.betAllin" : "replayApp.summary.bet", { who: who(actor), amount: inBigBlinds(onTable) }));
        break;
      case "RAISE":
        street.actions.push(t(allin ? "replayApp.summary.raiseAllin" : "replayApp.summary.raise", { who: who(actor), amount: inBigBlinds(onTable) }));
        break;
      default:
        break;
    }
  });
  streets.forEach((street) => {
    const label = t("replayApp.summary." + street.step.toLowerCase()) + (street.board !== null ? " (" + street.board + ")" : "");
    lines.push({ label, text: street.actions.length > 0 ? street.actions.join(", ") + "." : t("replayApp.summary.noAction") });
  });

  // the showdown: the cards shown; then who wins what
  const shows = last.players
    .filter((p) => p.atShowDown || p.showedCards)
    .map((p) => {
      const cards = knownCards(p.cards);
      return cards === null ? null : t("replayApp.summary.show", { who: who(p.pseudo), cards });
    })
    .filter((text) => text !== null);
  if (shows.length > 0) {
    lines.push({ label: t("replayApp.summary.showdown"), text: shows.join(", ") + "." });
  }
  const wins = last.players
    .filter((p) => p.eventType === "COLLECT" && Number(p.chipsOnTable) > 0)
    .map((p) => t("replayApp.summary.collect", { who: who(p.pseudo), amount: amount(Number(p.chipsOnTable)) }));
  if (wins.length > 0) {
    lines.push({ text: wins.join(", ") + "." });
  }
  return lines;
};

function HandSummary({ hand }) {
  const { t } = useTranslation();
  const [fullHand, setFullHand] = useState(null);
  const id = hand ? hand.id : "";
  const fullSession = Boolean(hand && hand.fullSession);

  useEffect(() => {
    setFullHand(null);
    if (!id || fullSession) {
      return undefined;
    }
    let cancelled = false;
    fetch(window.serverUrl + "/social/hand.php?id=" + encodeURIComponent(id))
      .then((r) => r.json())
      .then((_hand) => {
        if (!cancelled) {
          setFullHand(_hand);
        }
      })
      .catch((err) => {
        // never fetched again: the page does without the summary
        console.error("fetch problem", err);
      });
    return () => {
      cancelled = true;
    };
  }, [id, fullSession]);

  const lines = fullHand === null ? null : buildHandSummary(fullHand, t, i18n.language);
  if (lines === null || lines.length === 0) {
    return <></>;
  }
  return (
    <details className="hand-summary">
      <summary>{t("replayApp.summary.title")}</summary>
      <div className="hand-summary-content">
        {lines.map((line, index) => (
          <p key={index}>
            {line.label ? <span className="hand-summary-step">{line.label + t("replayApp.summary.colon")}</span> : null}
            {line.text}
          </p>
        ))}
      </div>
    </details>
  );
}

export default HandSummary;
